import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import { LoadingButton } from "@mui/lab";
import { Stack } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useContext } from "react";
import { useMutation } from "react-relay";
import { InternBuildActionsPanelAndroidMutation } from "./__generated__/InternBuildActionsPanelAndroidMutation.graphql";
import { InternBuildActionsPanelIOSMutation } from "./__generated__/InternBuildActionsPanelIOSMutation.graphql";
import SnackbarContext from "../../../contexts/SnackbarContext";

const pushiOSBuildMutation = graphql`
  mutation InternBuildActionsPanelIOSMutation(
    $input: KickoffIosAppBuildInput!
  ) {
    kickoffIosAppBuild(input: $input) {
      success
      message
      pipelineId
    }
  }
`;

const pushAndroidBuildMutation = graphql`
  mutation InternBuildActionsPanelAndroidMutation(
    $input: KickoffAndroidAppBuildInput!
  ) {
    kickoffAndroidAppBuild(input: $input) {
      success
      message
      pipelineId
    }
  }
`;

interface Props {
  brandId: string;
  onKickedOff?: () => void;
  size?: "small" | "medium";
}

const InternBuildActionsPanel = ({
  brandId,
  onKickedOff,
  size = "small",
}: Props) => {
  const snackbarContext = useContext(SnackbarContext);

  const isStagingEnvironment =
    window.location.hostname.includes("staging") ||
    window.location.hostname.includes("localhost");

  const [pushiOSBuild, isiOSPushInFlight] =
    useMutation<InternBuildActionsPanelIOSMutation>(pushiOSBuildMutation);

  const [pushAndroidBuild, isAndroidPushInFlight] =
    useMutation<InternBuildActionsPanelAndroidMutation>(
      pushAndroidBuildMutation
    );

  const handleiOSBuild = () => {
    pushiOSBuild({
      variables: {
        input: {
          brandId,
          versionBump: "AUTO",
          env: isStagingEnvironment ? "STAGING" : "PROD",
        },
      },
      onCompleted: (data, errors) => {
        if (errors || !data.kickoffIosAppBuild?.success) {
          snackbarContext?.openSnackbar(
            data.kickoffIosAppBuild?.message || "iOS Build Failed",
            "error"
          );
        } else {
          snackbarContext?.openSnackbar("iOS Build Kicked Off", "success");
          onKickedOff?.();
        }
      },
      onError: () => {
        snackbarContext?.openSnackbar("iOS Build Failed", "error");
      },
    });
  };

  const handleAndroidBuild = () => {
    pushAndroidBuild({
      variables: {
        input: {
          brandId,
          versionBump: "BUILD",
          env: isStagingEnvironment ? "STAGING" : "PROD",
          track: "PRODUCTION",
          // Draft so Intern Ready-to-Submit can complete the Play release.
          releaseStatus: "DRAFT",
        },
      },
      onCompleted: (data, errors) => {
        if (errors || !data.kickoffAndroidAppBuild?.success) {
          snackbarContext?.openSnackbar(
            data.kickoffAndroidAppBuild?.message || "Android Build Failed",
            "error"
          );
        } else {
          snackbarContext?.openSnackbar("Android Build Kicked Off", "success");
          onKickedOff?.();
        }
      },
      onError: () => {
        snackbarContext?.openSnackbar("Android Build Failed", "error");
      },
    });
  };

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <LoadingButton
        size={size}
        variant="contained"
        loading={isiOSPushInFlight}
        startIcon={<AppleIcon sx={{ fontSize: "1rem !important" }} />}
        onClick={handleiOSBuild}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.75rem",
          px: 1.5,
          bgcolor: "#111827",
          color: "#fff",
          boxShadow: "none",
          "&:hover": {
            bgcolor: "#1f2937",
            boxShadow: "none",
          },
        }}
      >
        Push iOS
      </LoadingButton>
      <LoadingButton
        size={size}
        variant="outlined"
        loading={isAndroidPushInFlight}
        startIcon={<AndroidIcon sx={{ fontSize: "1rem !important" }} />}
        onClick={handleAndroidBuild}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.75rem",
          px: 1.5,
          borderColor: "var(--intern-border-default)",
          color: "var(--intern-text-primary)",
          bgcolor: "var(--intern-bg-primary)",
          "&:hover": {
            borderColor: "#3DDC84",
            color: "#15803d",
            bgcolor: "rgba(61, 220, 132, 0.06)",
          },
        }}
      >
        Push Android
      </LoadingButton>
    </Stack>
  );
};

export default InternBuildActionsPanel;
